import React from 'react';
import { generateParentNTNsFromCases, setAnalyticsData } from '../../utils';
import {
  getActivities,
  getPersonData,
  getPreferences,
} from '../../utils/web-apis-client';
import * as dm from '../../data-models';
import { useVBCoverage } from '../../hooks/use-data';
import { log } from '../../utils/log';

// const log = new Logger\('contexts/person-data-context');

const PersonDataContext = React.createContext<{
  personData: dm.PersonData | undefined;
  setPersonData: React.Dispatch<React.SetStateAction<any>>;
  refreshPersonData: () => Promise<void>;
  refreshClaimsInProcess: () => Promise<void>;
  refreshCommunicationPreferences: () => Promise<void>;
  vbCoverageData: dm.ICoverageV1ResponseExt | undefined;
  vbLoading: boolean;
  analyticsDataReady: boolean;
}>({
  personData: undefined,
  setPersonData: () => {},
  refreshPersonData: async () => {},
  refreshClaimsInProcess: async () => {},
  refreshCommunicationPreferences: async () => {},
  vbCoverageData: undefined,
  vbLoading: false,
  analyticsDataReady: false,
});

function PersonDataProvider(
  p: Readonly<{ children: any; personData?: dm.PersonData }>,
) {
  const enhancePersonData = (personData?: dm.PersonData) => {
    if (!personData) {
      return personData;
    }

    const enhancedPersonData: dm.IGeneratedPersonData = { ...personData };

    enhancedPersonData.generated = enhancedPersonData.generated ?? {};

    enhancedPersonData.generated.eid = enhancedPersonData?.person?.eid ?? '';

    enhancedPersonData.generated.parentNTNs = generateParentNTNsFromCases(
      enhancedPersonData.cases,
    );

    try {
      setAnalyticsData(enhancedPersonData);
    } catch (ex) {
      log.error('Error setting analytics data:', ex);
    }

    return enhancedPersonData;
  };

  const addClaimsInProcessToPersonData = async (
    personData: dm.PersonData | dm.IGeneratedPersonData | undefined,
  ) => {
    if (!personData) {
      return personData;
    }
    try {
      const claimSubmissionsInProcess = await getActivities(
        ['CLAIMSUBMISSION', 'UPDATEDATA'],
        false,
      );

      const recentClaimsInProcess = claimSubmissionsInProcess.activities.filter(
        (act) => {
          const createdDate = new Date(act.createdDate);
          const oneDayAgo = new Date();
          oneDayAgo.setDate(oneDayAgo.getDate() - 1); // Subtract 1 day
          return createdDate >= oneDayAgo;
        },
      );

      const pd: dm.IGeneratedPersonData = { ...personData };
      pd.claimsInProcess = recentClaimsInProcess
        ? recentClaimsInProcess
            .filter((act) => isActivitySubmission(act))
            .map((act) => mapSubmissionActivityToClaimInProcess(act))
        : [];
      return pd;
    } catch (ex) {
      log.error('Error fetching claim submissions in process', ex);
      return personData;
    }
  };

  const { data: vbCoverageData, isLoading: vbLoading } = useVBCoverage();

  const [personData, setPersonData] = React.useState<dm.PersonData | undefined>(
    enhancePersonData(p.personData),
  );
  const [analyticsDataReady, setAnalyticsDataReady] = React.useState(false);

  const enrichedPersonData = React.useMemo(
    () =>
      personData?.person && vbCoverageData?.coverages
        ? {
            ...personData,
            person: {
              ...personData.person,
              vbCoverages: vbCoverageData.coverages,
            },
          }
        : personData,
    [personData, vbCoverageData?.coverages],
  );

  React.useEffect(() => {
    const initializeData = async () => {
      try {
        await Promise.all([
          refreshClaimsInProcess(),
          refreshCommunicationPreferences(),
        ]);
      } catch (error) {
        log.error('Failed to load data during initialization', error);
      } finally {
        setAnalyticsDataReady(true);
      }
    };

    void initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshClaimsInProcess = React.useCallback(async () => {
    const updatedPersonData = await addClaimsInProcessToPersonData(personData);
    setPersonData((prev) =>
      prev
        ? { ...prev, claimsInProcess: updatedPersonData?.claimsInProcess }
        : prev,
    );
    log.debug('Refreshed claims in process in personData context.');
  }, [personData]);

  const refreshPersonData = React.useCallback(async () => {
    const pd = await getPersonData();
    setPersonData(enhancePersonData(pd));
    await refreshClaimsInProcess();
    log.debug('Refreshed personData context.');
  }, [refreshClaimsInProcess]);

  const refreshCommunicationPreferences = async () => {
    try {
      const prefs = await getPreferences();
      setPersonData((prev) =>
        prev?.person
          ? {
              ...prev,
              person: { ...prev.person, preferences: prefs },
            }
          : prev,
      );
      log.debug('Refreshed communication preferences in personData context.');
    } catch (ex) {
      log.error('Error fetching communication preferences', ex);
    }
  };

  const contextValue = React.useMemo(
    () => ({
      personData: enrichedPersonData,
      setPersonData,
      refreshPersonData,
      refreshClaimsInProcess,
      refreshCommunicationPreferences,
      vbCoverageData,
      vbLoading,
      analyticsDataReady,
    }),
    [
      refreshClaimsInProcess,
      refreshPersonData,
      enrichedPersonData,
      vbCoverageData,
      vbLoading,
      analyticsDataReady,
    ],
  );

  return (
    <PersonDataContext.Provider value={contextValue}>
      {p.children}
    </PersonDataContext.Provider>
  );
}

// Accom submissions are sent through as UPDATEDATA activities, but this activity type encompasses many different operations
const isActivitySubmission = (activity: dm.IActivity) => {
  if (activity.type === 'CLAIMSUBMISSION') {
    return true;
  }
  if (activity.type === 'UPDATEDATA' && activity.dataType === 'ACCOMMODATION') {
    return true;
  }
  return false;
};

const mapSubmissionActivityToClaimInProcess = (
  submissionActivity: dm.IActivity,
) => {
  const createdDate = submissionActivity.createdDate;

  if (submissionActivity.type === 'CLAIMSUBMISSION') {
    const submissionReq: dm.ClaimSubmissionActivitySubmission | undefined =
      submissionActivity.submissionRequest;

    const questions: {
      fieldName: string;
      questionText?: string;
      answerText?: string;
    }[] = submissionReq ? submissionReq.questions : [];
    const leave = submissionReq
      ? submissionReq.leave
      : {
          reason: '',
          reasonQualifier1: '',
          reasonQualifier2: '',
          timeOffLeavePeriods: '',
          reducedSchedulePeriods: '',
          episodicLeavePeriods: '',
        };

    const allPeriods = [
      ...(leave.timeOffLeavePeriods || []),
      ...(leave.reducedSchedulePeriods || []),
      ...(leave.episodicLeavePeriods || []),
    ];

    let startDate: string | null = null;
    let endDate: string | null = null;

    if (allPeriods.length > 0) {
      const startDates = allPeriods
        .map((p) => p.startDate)
        .filter((d) => d)
        .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

      const endDates = allPeriods
        .map((p) => p.endDate)
        .filter((d) => d)
        .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

      startDate = startDates[0] || null;
      endDate = endDates.at(-1) || null;
    }

    return {
      createdDate: createdDate,
      startDate: startDate,
      endDate: endDate,
      reason: leave.reason || '',
      reasonQualifier1: leave.reasonQualifier1 || '',
      reasonQualifier2: leave.reasonQualifier2 || '',
      questions: questions,
    };
  } else {
    const questions: {
      fieldName: string;
      questionText?: string;
      answerText?: string;
    }[] = submissionActivity ? submissionActivity.json.questions : [];

    return {
      createdDate: createdDate,
      startDate: '',
      endDate: '',
      reason: 'Accommodation Request',
      reasonQualifier1: '',
      reasonQualifier2: '',
      questions: questions,
    };
  }
};

export {
  PersonDataContext,
  PersonDataProvider,
  mapSubmissionActivityToClaimInProcess,
};
